import { reportApiResponse } from "./api-responses";
import { env } from "./env";
import { reportUnauthorized } from "./unauthorized";

/**
 * The backend origin. Every request is cross-origin now (app.hrdocket.com →
 * api.hrdocket.com) and authenticated by a session cookie scoped to the shared
 * parent domain, so every call must send `credentials: "include"` and the
 * backend must allow this origin by name. See `api/src/app.ts`.
 */
export const API_BASE_URL: string = env.apiUrl;

/**
 * Shown when `fetch` itself fails, which in the browser means the request never
 * got an HTTP response: the API is down, the host is wrong, or a preflight was
 * rejected. Built from `API_BASE_URL` rather than naming a port, because it used
 * to read "running on port 3005" in 49 hand-written copies while the API had
 * long since moved to 8787 — so the one message a developer sees when nothing
 * works was sending them to the wrong port.
 */
export const OFFLINE_MESSAGE = `Can't reach the server. Make sure the backend is running at ${API_BASE_URL}.`;

export function apiUrl(path: string): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${suffix}`;
}

/**
 * Drop-in `fetch` for backend calls (pass it `apiUrl(...)`).
 *
 * In the Next.js app this juggled a server-side binding for the server-render
 * path and a same-origin proxy for the browser. Both are gone: these are SPAs,
 * so there is only ever the browser, and the one thing every call needs is the
 * cross-origin cookie.
 *
 * It is also where a lapsed session is noticed: every 401 is reported to
 * `unauthorized.ts`, and every response to `api-responses.ts`. Calls made with
 * a bare `fetch` bypass both, so backend calls should come through here.
 */
export async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, { credentials: "include", ...init });
  // A 401 here usually means the session ended mid-use; the handler installed
  // by the entry confirms that and sends the user to sign in.
  if (res.status === 401) reportUnauthorized(input);
  reportApiResponse(res, init);
  return res;
}

/**
 * Builds an absolute URL on *this app's* origin. Used for OAuth `callbackURL`
 * and email-verification `callbackURL` — better-auth resolves relative paths
 * against its own baseURL, which would land the user on the API host.
 */
export function appUrl(path: string): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${env.selfUrl}${suffix}`;
}
